import { useState, useEffect, useCallback, useMemo } from 'react';
import supabase, { orgId } from '../supabaseClient';
import { NM } from '../utils/constants';

const PEOPLE_KEY      = 'people_roster';
const DEPT_KEY        = 'departments';
const ONCOSTS_KEY     = 'employee_oncosts';
const PAY_RISE_KEY    = 'pay_rise';

export function defaultEmployee() {
  return {
    id: Math.random().toString(36).slice(2) + Date.now().toString(36),
    name: '', role: '', department: '',
    status: 'employee', // 'employee' | 'eor'
    salary: '', currency: '', // defaults to FC if blank
    startDate: { month: '', year: '' },
    endDate:   { month: '', year: '' },
  };
}

export function defaultDepartment() {
  return { id: Math.random().toString(36).slice(2) + Date.now().toString(36), name: '', type: 'overhead' }; // 'developer' | 'overhead'
}

/**
 * Calculate monthly cost for an employee in their local currency.
 * Returns array of NM monthly costs.
 */
export function calcEmployeeMonthlyCosts(emp, oncostsPct, payRisePct, payRiseMonth, startDate) {
  const salary = parseFloat(emp.salary) || 0;
  if (!salary) return Array(NM).fill(0);

  const oncosts = emp.status === 'employee' ? (parseFloat(oncostsPct) || 0) / 100 : 0;
  const totalSalary = salary * (1 + oncosts);
  const monthlyBase = totalSalary / 12;

  const payRise = (parseFloat(payRisePct) || 0) / 100;
  const riseMonth = parseInt(payRiseMonth) ?? null;

  return Array.from({ length: NM }, (_, i) => {
    // Check if employee is active this month
    const { startMonth, endMonth } = getEmployeeRange(emp, startDate);
    if (startMonth === null) return 0;
    if (i < startMonth) return 0;
    if (endMonth !== null && i > endMonth) return 0;

    // Partial month for start
    let fraction = 1;
    if (i === startMonth && emp.startDate?.day) {
      const daysInMonth = new Date(
        (startDate?.year || 2024) + Math.floor(((startDate?.month || 0) + i) / 12),
        ((startDate?.month || 0) + i) % 12 + 1, 0
      ).getDate();
      fraction = (daysInMonth - parseInt(emp.startDate.day) + 1) / daysInMonth;
    }
    // Partial month for end
    if (endMonth !== null && i === endMonth && emp.endDate?.day) {
      const daysInMonth = new Date(
        (startDate?.year || 2024) + Math.floor(((startDate?.month || 0) + i) / 12),
        ((startDate?.month || 0) + i) % 12 + 1, 0
      ).getDate();
      fraction = Math.min(fraction, parseInt(emp.endDate.day) / daysInMonth);
    }

    const base = monthlyBase * fraction;
    // Apply pay rise every 12 months from riseMonth onwards
    let withRise = base;
    if (riseMonth !== null && payRise > 0 && i >= riseMonth) {
      const yearsApplied = Math.floor((i - riseMonth) / 12) + 1;
      withRise = base * Math.pow(1 + payRise, yearsApplied);
    }
    return withRise;
  });
}

export function getEmployeeRange(emp, startDate) {
  let startMonth = null, endMonth = null;

  if (emp.startDate?.month !== '' && emp.startDate?.year !== '') {
    const sm = parseInt(emp.startDate.month);
    const sy = parseInt(emp.startDate.year);
    if (startDate) {
      startMonth = (sy - startDate.year) * 12 + sm - startDate.month;
    } else {
      startMonth = 0;
    }
  }

  if (emp.endDate?.month !== '' && emp.endDate?.year !== '') {
    const em = parseInt(emp.endDate.month);
    const ey = parseInt(emp.endDate.year);
    if (startDate) {
      endMonth = (ey - startDate.year) * 12 + em - startDate.month;
    } else {
      endMonth = NM - 1;
    }
  }

  return { startMonth, endMonth };
}

export default function usePeopleCosts() {
  const [employees, setEmployees]     = useState([]);
  const [departments, setDepartments] = useState([]);
  const [oncostsPct, setOncostsPct]   = useState('');
  const [payRisePct, setPayRisePct]   = useState('');
  const [payRiseMonth, setPayRiseMonth] = useState('');
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('app_settings').select('key,value')
        .eq('org_id', orgId)
        .in('key', [PEOPLE_KEY, DEPT_KEY, ONCOSTS_KEY, PAY_RISE_KEY]);

      (data || []).forEach(row => {
        try {
          const v = JSON.parse(row.value);
          if (row.key === PEOPLE_KEY)   setEmployees(v || []);
          if (row.key === DEPT_KEY)     setDepartments(v || []);
          if (row.key === ONCOSTS_KEY)  setOncostsPct(v?.pct || '');
          if (row.key === PAY_RISE_KEY) {
            setPayRisePct(v?.pct || '');
            setPayRiseMonth(v?.month ?? '');
          }
        } catch (_) {}
      });
      setLoading(false);
    }
    load();
  }, []);

  const save = useCallback(async (key, value) => {
    await supabase.from('app_settings').upsert(
      { org_id: orgId, key, value: JSON.stringify(value) },
      { onConflict: 'org_id,key' }
    );
  }, []);

  const updateEmployees = useCallback((emps) => {
    setEmployees(emps);
    save(PEOPLE_KEY, emps);
  }, [save]);

  const updateDepartments = useCallback((depts) => {
    setDepartments(depts);
    save(DEPT_KEY, depts);
  }, [save]);

  const updateOncosts = useCallback((pct) => {
    setOncostsPct(pct);
    save(ONCOSTS_KEY, { pct });
  }, [save]);

  const updatePayRise = useCallback((pct, month) => {
    setPayRisePct(pct);
    setPayRiseMonth(month);
    save(PAY_RISE_KEY, { pct, month });
  }, [save]);

  return {
    employees, departments, oncostsPct, payRisePct, payRiseMonth, loading,
    updateEmployees, updateDepartments, updateOncosts, updatePayRise,
  };
}
